import { useMediaQuery, useTheme } from "@mui/material";
import React, {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  createContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { BentoBrand } from "schemas/dashboard";

import {
  AssistantEntityContext,
  PromptPage,
} from "features/Home/Sidebar/AssistantPreview/api";
import { trackEvent } from "utils/tracking";

export type AssistantOpenContext = {
  promptPage?: PromptPage;
  entity?: AssistantEntityContext;
  source?: string;
  initialMessage?: string;
};

interface DiscoverViewContextInterface {
  tabWidth: number;
  discoverTab: DiscoverTabView | null;
  setDiscoverTab: (tab: DiscoverTabView | null) => void;
  lowerWidth: boolean;
  displayedBrandInfo: boolean;
  setBrandNavigationHistory: Dispatch<SetStateAction<BentoBrand[]>>;
  brandNavigationHistory: BentoBrand[];
  openVaSettings: boolean;
  setOpenVaSettings: Dispatch<SetStateAction<boolean>>;
  assistantOpenContext: AssistantOpenContext | null;
  setAssistantOpenContext: (context: AssistantOpenContext | null) => void;
  openAssistant: (context?: AssistantOpenContext) => void;
  assistantAutoIntroAttempted: MutableRefObject<boolean>;
}

const defaultContextMissingFunction = () => {
  throw new Error("context is missing");
};

const defaultInterface = {
  tabWidth: 350,
  discoverTab: null,
  setDiscoverTab: defaultContextMissingFunction,
  lowerWidth: false,
  displayedBrandInfo: true,
  setBrandNavigationHistory: defaultContextMissingFunction,
  brandNavigationHistory: [],
  openVaSettings: false,
  setOpenVaSettings: defaultContextMissingFunction,
  assistantOpenContext: null,
  setAssistantOpenContext: defaultContextMissingFunction,
  openAssistant: defaultContextMissingFunction,
  assistantAutoIntroAttempted: { current: false },
};

export enum DiscoverTabView {
  CHAT = "Chat",
  SAVED_BRANDS = "Saved Brands",
  TEMPLATES = "Templates",
  METRICS = "Metrics",
  VA_SETTINGS = "VA Settings",
  USER_REQUESTS = "Wishlist",
  UPLOAD_BRANDS = "Upload Brands",
  ASSISTANT = "Assistant",
}

const DiscoverViewContext =
  createContext<DiscoverViewContextInterface>(defaultInterface);

interface DiscoverViewProviderProps {
  children: React.ReactNode;
}

const DiscoverViewProvider = ({ children }: DiscoverViewProviderProps) => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));

  const tabWidth = 350;

  const [discoverTab, setDiscoverTabView] = useState<DiscoverTabView | null>(
    null,
  );
  const [brandNavigationHistory, setBrandNavigationHistory] = useState<
    BentoBrand[]
  >([]);
  const [openVaSettings, setOpenVaSettings] = useState<boolean>(false);
  const [assistantOpenContext, setAssistantOpenContext] =
    useState<AssistantOpenContext | null>(null);
  const assistantAutoIntroAttempted = useRef(false);

  const openAssistant = (context?: AssistantOpenContext) => {
    setAssistantOpenContext(context ?? null);
    setDiscoverTabView(DiscoverTabView.ASSISTANT);
    trackEvent("Assistant Panel Opened", {
      Source: context?.source ?? "Unknown",
    });
  };

  const isMobileScreenWithTab = useMediaQuery("(max-width:1400px)");
  const lowerWidth = discoverTab !== null && isMobileScreenWithTab;
  const displayedBrandInfo = useMediaQuery(
    discoverTab !== null ? "(min-width:1200px)" : "(min-width:900px)",
  );

  // Discover tab will act like a toggle, except when opening AI chat
  const setDiscoverTab = (tab: DiscoverTabView | null) => {
    if (tab === null || (discoverTab === tab && tab !== DiscoverTabView.CHAT)) {
      setDiscoverTabView(null);
    } else {
      setDiscoverTabView(tab);
    }
  };

  useEffect(() => {
    if (isMobileScreen) {
      setDiscoverTabView((prev) =>
        prev === DiscoverTabView.ASSISTANT ? prev : null,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobileScreen]);

  // Clear the assistant open context whenever the assistant tab isn't active,
  // so reopening from the sidebar doesn't carry over a stale entity / page.
  useEffect(() => {
    if (discoverTab !== DiscoverTabView.ASSISTANT && assistantOpenContext) {
      setAssistantOpenContext(null);
    }
  }, [discoverTab, assistantOpenContext]);

  return (
    <DiscoverViewContext.Provider
      value={{
        discoverTab,
        setDiscoverTab,
        tabWidth,
        lowerWidth,
        displayedBrandInfo,
        brandNavigationHistory,
        setBrandNavigationHistory,
        openVaSettings,
        setOpenVaSettings,
        assistantOpenContext,
        setAssistantOpenContext,
        openAssistant,
        assistantAutoIntroAttempted,
      }}
    >
      {children}
    </DiscoverViewContext.Provider>
  );
};

export { DiscoverViewProvider, DiscoverViewContext };
